import { buildFormOutline } from './form_outline';

/**
 * Projects a form's outline from inside the form manager and hands it to
 * whoever opened it.
 *
 * This exists because the projection cannot be done on the server. Section
 * numbering is not in the template — it is composed at render time by the list
 * engine from `ordered_listitem` — so the only place a numbered, sectioned form
 * exists is a browser that rendered one. The playbook editor opens
 * `/manage_forms/forms/:id/edit?playbook_outline=1` in a hidden iframe, this
 * runs there, and the result goes back by postMessage.
 *
 * Inert unless asked. The form manager is a working screen and must not do any
 * of this for someone editing a form.
 */

export const OUTLINE_MESSAGE = 'lp:form-outline';
const TRIGGER_PARAM = 'playbook_outline';

// The list engine composes numbering after Angular boots, in more than one
// pass, and emits no event this can wait on. So: settled means the paragraph
// count has stopped moving AND numbering has actually appeared. Both matter —
// a count that is stable because nothing has rendered yet is not a settled
// document, and an outline projected then would be silently short.
const POLL_MS = 500;
const STABLE_POLLS = 3;
const TIMEOUT_MS = 120000;

function paragraphCount(doc) {
  const lease = doc.querySelector('.lease');
  return lease ? lease.querySelectorAll('p[pid]').length : 0;
}

function numberingComposed(doc) {
  const lease = doc.querySelector('.lease');
  return Boolean(lease && lease.querySelector('p[data-list-set-level]'));
}

export function waitForRender(doc, { onSettled, onTimeout }) {
  let previous = -1;
  let stable = 0;
  const startedAt = Date.now();

  const timer = setInterval(function() {
    const count = paragraphCount(doc);

    if (count > 0 && count === previous) stable += 1;
    else stable = 0;
    previous = count;

    if (stable >= STABLE_POLLS && numberingComposed(doc)) {
      clearInterval(timer);
      onSettled();
      return;
    }

    if (Date.now() - startedAt > TIMEOUT_MS) {
      clearInterval(timer);
      onTimeout();
    }
  }, POLL_MS);

  return () => clearInterval(timer);
}

function formIdFromLocation(location) {
  const match = /\/forms\/(\d+)\//.exec(location.pathname || '');
  return match ? Number(match[1]) : null;
}

export function shouldProject(location) {
  try {
    return new URLSearchParams(location.search).get(TRIGGER_PARAM) === '1';
  } catch (e) {
    return false;
  }
}

function post(message) {
  if (!window.parent || window.parent === window) return;
  // Same origin only. The outline is a customer's own lease language, and it
  // has no business being readable by a page from anywhere else.
  window.parent.postMessage(message, window.location.origin);
}

export function startFormOutlineHarness(doc = document, location = window.location) {
  if (!shouldProject(location)) return null;

  const formId = formIdFromLocation(location);

  return waitForRender(doc, {
    onSettled() {
      try {
        const outline = buildFormOutline(doc);
        post({
          type: OUTLINE_MESSAGE,
          formId: formId,
          outline: outline,
          error: null,
        });
      } catch (e) {
        post({
          type: OUTLINE_MESSAGE,
          formId: formId,
          outline: null,
          error: 'This form could not be read in the browser.',
        });
      }
    },
    onTimeout() {
      // Said rather than swallowed: a form that never finishes rendering looks
      // exactly like one still rendering, and the screen would wait forever.
      post({
        type: OUTLINE_MESSAGE,
        formId: formId,
        outline: null,
        error: 'This form did not finish rendering.',
      });
    },
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => startFormOutlineHarness());
  } else {
    startFormOutlineHarness();
  }
}
